import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { SaasError, saasPasswordResetConfirm } from '../lib/saas-client';

export function ResetPasswordPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user) return <Navigate to="/account" replace />;

  if (!token) {
    return (
      <div className="auth-page">
        <section className="panel auth-panel">
          <header className="page-header">
            <h1>Reset link missing</h1>
            <p>
              This page expects a one-time token in the URL. Request a fresh reset
              link from the forgot-password page.
            </p>
          </header>
          <p className="auth-switch">
            <Link to="/forgot-password">Request a reset link →</Link>
          </p>
        </section>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await saasPasswordResetConfirm(token, password);
      navigate('/login?reset=ok', { replace: true });
    } catch (err) {
      setError(err instanceof SaasError ? err.message : 'Reset failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <section className="panel auth-panel">
        <header className="page-header">
          <h1>Set a new password</h1>
          <p>Pick a new password. Existing sessions on this account will be signed out.</p>
        </header>
        <form onSubmit={onSubmit} className="auth-form">
          <label>
            <span>New password</span>
            <input
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <small className="muted">At least 12 characters.</small>
          </label>
          <label>
            <span>Confirm new password</span>
            <input
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Updating…' : 'Update password'}
          </button>
        </form>
        <p className="auth-switch">
          <Link to="/login">← Back to sign in</Link>
        </p>
      </section>
    </div>
  );
}
